import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {Form} from "semantic-ui-react";
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { useMessageResources, useTextResources } from '../../../language';
import { useCallback } from "react";
import useBoolean from "../../../hooks/useBoolean";
import useSafeState from "../../../hooks/useSafeState";
import TextInput from "../../../components/shared/controlled/TextInput";
import TaxIdentityHotelsTransfer from "../components/TaxIdentityHotelsTransfer";
import NextUsersDropDown from "../../../components/shared/NextUsersDropDown";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { onWarning } from '../../../components/shared/messages';
import { v4 as uuidv4 } from 'uuid';

const defaultState = {
    Id: 0,
    Name: '',
    UserId: undefined,
    IsActive: true,
    TaxIdentityHotels: [],
};

const usersDropDownSpec = {
    IsActive: true,
    HasEmail: true,
    IsAdmin: false,
};

const EditTaxIdentityPopup = ({
    open,
    taxIdentity,
    save,
    onClose,
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const currentHotel = useCurrentHotel();
    const isCreate = !taxIdentity;
    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);
    const [taxIdentityState, setTaxIdentityState] = useSafeState(defaultState);

    const resetState = useCallback(() => {
        setTaxIdentityState({
            HotelChainId: currentHotel.HotelChainId,
            ClientId: uuidv4(),
            ...defaultState,
            ...taxIdentity,
        });
    }, [currentHotel.HotelChainId, taxIdentity, setTaxIdentityState]);

    useEffect(() => {
        resetState();
    }, [resetState]);

    const setName = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            Name: value,
        }));
    }, [setTaxIdentityState]);

    const setUserId = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            UserId: value ? value : null,
        }));
    }, [setTaxIdentityState]);

    const setTaxIdentityHotels = useCallback((value) => {
        setTaxIdentityState((prevState) => ({
            ...prevState,
            TaxIdentityHotels: value,
        }));
    }, [setTaxIdentityState]);

    const isValid = useCallback((taxIdentityState) => {
        if (!taxIdentityState.Name || !taxIdentityState.TaxIdentityHotels.length) {
            onWarning(message("AllFieldsAreRequired"));
            return false;
        }

        return true;
    }, [message]);
    
    const handleSave = useCallback(async () => {
        if (!isValid(taxIdentityState)) {
            return;
        }

        try {
            setLoading();
            await save(taxIdentityState);
        } catch {
            return false;
        } finally {
            resetLoading();
        }

        return true;
    }, [taxIdentityState, isValid, resetLoading, save, setLoading]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSaveAndCreateNew = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && resetState();
        return isSaved;
    }, [handleSave, resetState]);

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && handleClose();
    }, [handleSave, handleClose]);

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={`${isCreate ? text("Create") : text("Edit")} ${text("TaxIdentity")}`}
            onClose={handleClose}
            onSave={isCreate ? handleSaveAndCreateNew : undefined}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
        >
            <Form>
                <Form.Field
                    label={text("Name")} 
                    control={TextInput}
                    onBlur={setName}
                    value={taxIdentityState.Name}
                    placeholder={text("Name")}
                    maxLength={100}
                />
                <Form.Field
                    label={text("User")}
                    control={NextUsersDropDown}
                    spec={usersDropDownSpec}
                    onChange={setUserId}
                    value={taxIdentityState.UserId}
                    placeholder={text("User")}
                    search
                    clearable
                />,
                <Form.Field
                    label={text("TaxIdentityHotels")}
                    control={TaxIdentityHotelsTransfer}
                    taxIdentityId={taxIdentityState.Id}
                    taxIdentityClientId={taxIdentityState.ClientId}
                    onChange={setTaxIdentityHotels}
                    value={taxIdentityState.TaxIdentityHotels}
                />
            </Form>
        </EditEntityPopup>
    );
};

EditTaxIdentityPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    taxIdentity: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        TaxIdentityHotels: PropTypes.arrayOf(PropTypes.shape({
            Id: PropTypes.number.isRequired,
            TaxIdentityId: PropTypes.number.isRequired,
            HotelId: PropTypes.number.isRequired,
        })),
    }),
    onClose: PropTypes.func.isRequired,
    save: PropTypes.func,
};

EditTaxIdentityPopup.defaultProps = {
    open: false,
    taxIdentity: undefined,
    onClose: () => {},
    save: () => Promise.resolve(),
}

export default EditTaxIdentityPopup;